import { RARITY_COLORS, type Rarity } from '@shefira/shared';
import type Phaser from 'phaser';

import { animate, dot, WHITE } from './fx-kit.js';

/**
 * The column of light over a rare drop (ADR 0049), in its rarity's colour,
 * so a good find is seen from across the screen before its name is read. A
 * common drop has none: the beam means something only if it is rare.
 */

/** How tall each rarity's column rises, in pixels; none for a common find. */
const BEAM_HEIGHT: Record<Rarity, number> = {
  common: 0,
  rare: 22,
  epic: 30,
  legendary: 40,
};
/** Each rarity's column breathes at this pace, a legendary faster. */
const PULSE_MS: Record<Rarity, number> = { common: 0, rare: 1400, epic: 1100, legendary: 800 };

export function hasBeam(rarity: Rarity): boolean {
  return BEAM_HEIGHT[rarity] > 0;
}

/**
 * The column itself, drawn once into a graphics whose feet are at (0, 0):
 * three pixels wide with a bright core, fading row by row as it rises.
 * Returned to be added to the drop's container, under its icon.
 */
export function lootBeam(scene: Phaser.Scene, rarity: Rarity): Phaser.GameObjects.Graphics {
  const color = RARITY_COLORS[rarity];
  const height = BEAM_HEIGHT[rarity];
  const graphics = scene.add.graphics();
  for (let row = 0; row < height; row += 1) {
    const fade = 1 - row / height;
    graphics.fillStyle(color, 0.55 * fade);
    graphics.fillRect(-1, -row - 1, 3, 1);
    graphics.fillStyle(WHITE, 0.7 * fade * fade);
    graphics.fillRect(0, -row - 1, 1, 1);
  }
  // A glow on the ground, wider than the column.
  graphics.fillStyle(color, 0.35);
  graphics.fillRect(-3, -1, 7, 1);
  scene.tweens.add({
    targets: graphics,
    alpha: 0.45,
    duration: PULSE_MS[rarity],
    yoyo: true,
    repeat: -1,
    ease: 'Sine.easeInOut',
  });
  return graphics;
}

/**
 * The moment a rare drop lands: sparks shoot up the column and scatter, once.
 * `feet` in world pixels; the sparks draw over the drop.
 */
export function lootBurst(
  scene: Phaser.Scene,
  rarity: Rarity,
  feet: { x: number; y: number },
): void {
  const color = RARITY_COLORS[rarity];
  const height = BEAM_HEIGHT[rarity];
  const sparks = rarity === 'legendary' ? 10 : rarity === 'epic' ? 8 : 6;
  animate(scene, feet.y + 2, 700, (graphics, progress) => {
    for (let spark = 0; spark < sparks; spark += 1) {
      const angle = (spark / sparks) * Math.PI * 2;
      const rise = progress * height;
      const spread = Math.sin(progress * Math.PI) * 6;
      const x = feet.x + Math.cos(angle) * spread;
      const y = feet.y - rise * (0.6 + 0.4 * ((spark % 3) / 2));
      if (progress < 0.85 || spark % 2 === 0) dot(graphics, x, y, spark % 3 === 0 ? WHITE : color);
    }
  });
}
